import {JournalPhotoStatus} from './journal-photo.js';

function code(error,fallback){
  return String(error?.code||error?.message||fallback).slice(0,80);
}

function payloadFile(payload){
  if(!payload)return null;
  if(payload.bytes){
    if(typeof Blob!=='undefined'){
      return new Blob([payload.bytes],{type:payload.mimeType});
    }
    return payload.bytes;
  }
  return payload.fallbackFile||null;
}

export function createJournalPhotoFlow({
  store,
  storage=null,
  remote=null,
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(!store?.create||!store?.get||!store?.savePayload||!store?.getPayload||!store?.removePayload
    ||!store?.listPendingUploads||!store?.markUploadFailed||!store?.markPendingMetadata
    ||!store?.markMetadataFailed||!store?.markUploaded||!store?.removeLocal){
    throw new TypeError('Journal photo store is required');
  }

  async function persistMetadata(photo){
    if(!remote?.insert){
      return Object.freeze({
        ok:false,
        status:'remote_unavailable',
        retryable:true,
        requiresFile:false,
        photo
      });
    }
    try{
      await remote.insert(photo);
      const saved=await store.markUploaded(photo.id,photo.storagePath);
      return Object.freeze({ok:true,status:'uploaded',retryable:false,requiresFile:false,photo:saved});
    }catch(error){
      const saved=await store.markMetadataFailed(photo.id,code(error,'JOURNAL_PHOTO_METADATA_FAILED'));
      return Object.freeze({ok:false,status:'metadata_failed',retryable:true,requiresFile:false,photo:saved});
    }
  }

  async function resolveFile(id,file){
    if(file!=null){
      await store.savePayload(id,file);
    }
    const payload=await store.getPayload(id);
    return payloadFile(payload);
  }

  async function attemptUpload(id,file=null){
    const current=await store.get(id);
    if(!current)throw new Error('JOURNAL_PHOTO_NOT_FOUND');

    if(current.storagePath&&current.photoStatus!==JournalPhotoStatus.UPLOADED){
      // storagePath is checkpointed only after binary upload succeeds. Even if
      // a later local bookkeeping step failed, never re-upload that binary;
      // finish or retry metadata instead.
      return persistMetadata(current);
    }

    if(!isOnline()){
      return Object.freeze({ok:true,status:'saved_offline',retryable:true,requiresFile:false,photo:current});
    }

    if(!storage?.upload){
      return Object.freeze({ok:false,status:'remote_unavailable',retryable:true,requiresFile:false,photo:current});
    }

    const durableFile=await resolveFile(id,file);
    if(durableFile==null){
      return Object.freeze({ok:false,status:'file_missing',retryable:false,requiresFile:true,photo:current});
    }

    try{
      const result=await storage.upload({photo:current,file:durableFile});
      const pending=await store.markPendingMetadata(current.id,result.path);
      await store.removePayload(current.id);
      return persistMetadata(pending);
    }catch(error){
      const saved=await store.markUploadFailed(current.id,code(error,'JOURNAL_PHOTO_UPLOAD_FAILED'));
      return Object.freeze({ok:false,status:'upload_failed',retryable:true,requiresFile:false,photo:saved});
    }
  }

  async function attach(input,{file=null}={}){
    const photo=await store.create(input);
    try{
      if(file==null)throw new TypeError('Photo file is required');
      await store.savePayload(photo.id,file);
    }catch(error){
      await store.removeLocal(photo.id);
      throw error;
    }

    if(!isOnline()){
      return Object.freeze({ok:true,status:'saved_offline',retryable:true,requiresFile:false,photo});
    }
    return attemptUpload(photo.id);
  }

  async function retry(id,{file=null}={}){
    return attemptUpload(id,file);
  }

  async function retryPending(){
    if(!isOnline())return Object.freeze({status:'offline',processed:0,uploaded:0,retries:0,results:Object.freeze([])});
    const pending=await store.listPendingUploads();
    const results=[];
    for(const photo of pending){
      try{
        results.push(await attemptUpload(photo.id));
      }catch(error){
        results.push(Object.freeze({
          ok:false,
          status:'retry_error',
          retryable:true,
          photoId:photo.id,
          errorCode:code(error,'JOURNAL_PHOTO_RETRY_FAILED')
        }));
      }
    }
    return Object.freeze({
      status:'processed',
      processed:results.length,
      uploaded:results.filter(result=>result.ok&&result.status==='uploaded').length,
      retries:results.filter(result=>!result.ok&&result.retryable).length,
      results:Object.freeze(results)
    });
  }

  async function remove(id){
    const photo=await store.get(id);
    if(!photo)return Object.freeze({ok:true,status:'already_absent'});
    if(!isOnline()&&photo.storagePath){
      return Object.freeze({ok:false,status:'offline_delete_pending',retryable:true,photo});
    }
    try{
      if(photo.storagePath){
        if(!remote?.remove||!storage?.remove){
          return Object.freeze({ok:false,status:'remote_unavailable',retryable:true,photo});
        }
        await remote.remove(photo);
        await storage.remove({photo});
      }
      await store.removeLocal(photo.id);
      return Object.freeze({ok:true,status:'deleted'});
    }catch(error){
      const saved=await store.markDeleteFailed(photo.id,code(error,'JOURNAL_PHOTO_DELETE_FAILED'));
      return Object.freeze({ok:false,status:'delete_failed',retryable:true,photo:saved});
    }
  }

  return Object.freeze({attach,retry,retryPending,remove});
}
